<template>
  <article
    :key="tileUniqueId"
    v-observe-visibility="{
      callback: handleScrollIntoView,
      once: !virtualizeWhenOffscreen,
      intersection: virtualizeWhenOffscreen ? { rootMargin: '200% 0px' } : {},
    }"
    :data-key="tileUniqueId"
    :class="[
      bemBlockMod({ 'quick-shop-opened': quickShopOpened }),
      'product-tile',
      'product-detail',
      'product-detail-vue',
    ]"
    :style="placeholderStyle"
    :data-pid="product.id"
    :data-context="context"
    :data-is-recommendation-from-einstein="isRecommendationFromEinstein"
    itemscope=""
    itemtype="http://schema.org/Product"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
    @mousedown="mousedownHandler"
    @mousemove="mousemoveHandler"
  >
    <template v-if="isContentActive">
      <BaseProductDataForSfra
        v-if="!hasPlpSliderBannerContext"
        :product="product"
      />
      <meta itemprop="sku" :content="product.id" />
      <meta itemprop="mpn" :content="product.id" />
      <meta
        v-if="product.longDescription"
        itemprop="description"
        :content="product.longDescription"
      />
      <meta itemprop="brand" content="BIMBA Y LOLA" />
      <BaseProductTileImage
        :are-carousel-controls-shown="areCarouselControlsShown"
        :class="bemElement('image')"
        :context="context"
        :force-main-image-to-load="forceMainImageToLoad"
        :has-carousel="hasCarousel"
        :product="product"
        :url="url"
        @click="viewProductDetails"
        @load-main-image="$emit('load-main-image', $event)"
      />
      <BaseTag
        v-if="showIframeTag"
        :class="[bemElement('main-label'), 'iframe-tag']"
        :product="product"
        type="grid"
        subtype="secondary"
        :text="$msg('tag.iframe', 'product')"
        :is-custom="true"
      />
      <div v-if="!minimal" :class="bemElement('body')">
        <BaseTag
          v-if="secondaryTag"
          :class="bemElement('main-label', 'overlap')"
          :product="product"
          type="grid"
          subtype="secondary"
          :text="secondaryTag"
          :text-color="secondaryTagColor"
          :is-custom="isCustomTag"
        />

        <BaseTag
          v-if="primaryTag"
          :class="bemElement('main-label')"
          :product="product"
          type="grid"
          subtype="primary"
          :text="primaryTag"
        />

        <BaseProductTileName
          v-if="!hideProductNames"
          :class="bemElement('name')"
          :product="product"
          :url="url"
          :line-clamp="tileNameLineClampNumber"
          @click="viewProductDetails"
        />

        <BasePrice
          v-if="!hidePrices && product.price"
          :class="bemElement('price')"
          :product="product"
          :show-previous-price-msg="showPreviousPriceMsg"
        />

        <BaseProductTileQuickShop
          v-if="showQuickShop"
          :context="context"
          :class="bemElement('quick-shop', { opened: quickShopOpened })"
          :product="product"
          @open="() => (quickShopOpened = true)"
          @close="() => (quickShopOpened = false)"
        />
      </div>

      <BaseProductTileQuickShop
        v-if="showFullTileQuickShop"
        :class="bemElement('quick-shop', { opened: quickShopOpened })"
        :context="context"
        :product="product"
        @open="() => (quickShopOpened = true)"
        @close="() => (quickShopOpened = false)"
      />

      <BaseProductColorSwatches
        v-if="showSwatches"
        :class="bemElement('swatches')"
        :product="product"
        :show-current="true"
        :pre-expanded="product.preExpanded || false"
        @select="updateProductColorVariant"
      />
    </template>
  </article>
</template>

<script>
import bemClassesMixin from '../mixins/bemClassesMixin';
import analyticsMixin from '../mixins/analyticsMixin';
import checkVisibilityMixin from '../mixins/checkVisibilityMixin';
import clickedWithoutDraggingMixin from '../mixins/clickedWithoutDraggingMixin';
import productPropBaseMixin from '../mixins/productPropBaseMixin';
import productTagsMixin from '../mixins/productTagsMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import { customizableProductMixin } from '../mixins/customizableProductMixin';
import BaseProductColorSwatches from './BaseProductColorSwatches.vue';
import BasePrice from './BasePrice.vue';
import BaseProductDataForSfra from './BaseProductDataForSfra.vue';
import BaseProductTileImage from './BaseProductTileImage.vue';
import BaseTag from './BaseTag.vue';
import BaseProductTileName from './BaseProductTileName.vue';
import BaseProductTileQuickShop from './BaseProductTileQuickShop.vue';
import { types } from '../store';
import getFrontConfig from '../../common/getFrontConfig';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';

// eslint-disable-next-line no-unused-vars
import jsdocTypes from '../../../../../jsdoc-types';

const { searchDataStorage } = require('../../analytics/stores/searchData');

const analyticsEventBus = getAnalyticsEventBus();

const {
  vue: {
    BaseProductTile: { context },
    TheProductList: {
      bannerTypes: { plpMobileSlider },
    },
  },
  responsive: { bp_575: bp575 },
  isAppExclusiveEnabled,
  isMarketWithoutSale,
} = getFrontConfig();

export default {
  name: 'BaseProductTile',

  components: {
    BaseProductColorSwatches,
    BasePrice,
    BaseProductDataForSfra,
    BaseProductTileImage,
    BaseTag,
    BaseProductTileName,
    BaseProductTileQuickShop,
  },

  mixins: [
    analyticsMixin,
    bemClassesMixin,
    checkVisibilityMixin,
    clickedWithoutDraggingMixin,
    productPropBaseMixin,
    productTagsMixin,
    windowResizeMixin,
    customizableProductMixin,
  ],

  props: {
    areCarouselControlsShown: {
      default: true,
      type: Boolean,
    },
    /**
     * Position of this tile within its parent BaseCarousel (zero-based).
     * Only relevant when context is plpSliderBanner.
     */
    carouselIndex: {
      type: Number,
      default: null,
      required: false,
    },
    context: {
      type: String,
      required: false,
      default: context.plpGrid,
    },
    forceMainImageToLoad: {
      default: false,
      required: false,
      type: Boolean,
    },
    gridIndex: {
      type: Number,
      required: true,
    },
    gridType: {
      type: String,
      validator: (prop) =>
        ['crossSelling', 'productList', 'search'].includes(prop),
      required: false,
      default: null,
    },
    hasCarousel: {
      type: Boolean,
      default: true,
      required: false,
    },
    /**
     * Hide prices in tile body.
     * Mainly used for PLP Sliders banners configured in MW.
     */
    hidePrices: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Hide product names in tile body.
     * Mainly used for PLP Sliders banners configured in MW.
     */
    hideProductNames: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Hide the primary and secondary tags if it is true.
     */
    hideTags: {
      type: Boolean,
      default: false,
      required: false,
    },
    isRecommendationFromEinstein: {
      type: Boolean,
      default: false,
    },
    keyid: {
      type: String,
      default: '',
      required: false,
    },
    minimal: {
      type: Boolean,
      default: false,
      required: false,
    },
    showSwatches: {
      type: Boolean,
      default: true,
      required: false,
    },
    /**
     * When true, tiles that scroll far off-screen will have their inner
     * content destroyed (v-if="false") and replaced by a fixed-height
     * empty <article> shell. This frees DOM nodes, decoded image bitmaps,
     * child component instances, observers, and event listeners —
     * dramatically reducing memory usage in long infinite-scroll lists.
     *
     * The article element itself stays in the DOM so the grid layout and
     * scroll position are preserved via a cached min-height.
     *
     * A 200% viewport rootMargin buffer ensures the swap happens well
     * outside the visible area.
     *
     * On window resize (e.g. device rotation), the cached height is
     * invalidated so it will be recalculated when the tile next enters
     * the viewport buffer.
     */
    virtualizeWhenOffscreen: {
      type: Boolean,
      default: false,
      required: false,
    },
    url: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    /** @type {boolean} Whether the tile inner content should be rendered */
    isContentActive: true,
    /** @type {number|null} Cached height in px to hold space when content is unloaded */
    cachedHeight: null,
    /** @type {boolean} True until the tile enters the viewport for the first time */
    isFirstTimeInViewport: true,
    clickWithoutDrag: false,
    dataChangeBreakpoint: bp575,
    hover: false,
    isMarketWithoutSale: undefined,
    quickShopOpened: false,
  }),

  computed: {
    /**
     * Inline style applied to the <article> to preserve its height
     * while inner content is destroyed.
     *
     * @returns {Object|null} Style object with `minHeight`, or `null` if not applicable.
     * @property {string} [return.minHeight] Calculated minimum height in pixels.
     */
    placeholderStyle() {
      if (!this.isContentActive && this.cachedHeight) {
        return {
          minHeight: `${this.cachedHeight}px`,
        };
      }
      return null;
    },

    hasPlpSliderBannerContext() {
      return this.context === context.plpSliderBanner;
    },

    tileUniqueId() {
      return this.keyid || this.upid;
    },

    showIframeTag() {
      return (
        !this.minimal &&
        !this.hideTags &&
        this.iframeUrl &&
        !this.product?.bylActionableLink
      );
    },

    primaryTag() {
      let text;
      const hideTag = this.hideTags || this.isMarketWithoutSale;
      if (hideTag) {
        // Quick return: NO TAG
        return text;
      }
      if (this.isSoldOut) {
        if (this.isComingSoon) {
          text = this.$msg('tag.coming', 'product');
        } else {
          text = this.$msg('tag.coming_unavailable', 'product');
        }
      }
      return text;
    },

    secondaryTag() {
      let text;
      const hideTag = this.hideTags || this.hasPlpSliderBannerContext;
      if (hideTag) {
        // Quick return: NO TAG
        return text;
      }
      if (this.isCustomTag && this.isCustomTagVisibleInCategory) {
        text = this.customTag.name;
      }
      if (this.isOnlineExclusive) {
        text = this.$msg('tag.exclusive', 'product');
      }
      return text;
    },

    secondaryTagColor() {
      return (this.isCustomTagVisibleInCategory && this.customTag?.color) || '';
    },

    shouldOpenModalForPDP() {
      return this.isBreakpointMobile && this.gridType === 'search';
    },

    showQuickShop() {
      return (
        this.product.productType !== 'set' &&
        this.product.available &&
        !this.hasPlpSliderBannerContext &&
        !this.showFullTileQuickShop &&
        !this.isAppExclusive &&
        !this.isCustomizable
      );
    },

    showFullTileQuickShop() {
      return this.context === context.minicartSlider;
    },

    isAppExclusive() {
      return isAppExclusiveEnabled && (this.product?.bylAppExclusive ?? false);
    },

    tileNameLineClampNumber() {
      return this.showFullTileQuickShop ? 1 : 2;
    },

    showPreviousPriceMsg() {
      return [
        context.crossSellingGrid,
        context.plpGrid,
        context.plpGridFullWidth,
        context.plpSliderBanner,
      ].includes(this.context);
    },
  },

  watch: {
    /**
     * windowResizeMixin exposes `windowWidth`. When it changes (resize,
     * orientation change), invalidate the cached height so the tile will
     * be measured again with the new layout when it next becomes active.
     */
    windowWidth() {
      if (this.virtualizeWhenOffscreen) {
        this.cachedHeight = null;
      }
    },
  },

  created() {
    this.isMarketWithoutSale = isMarketWithoutSale;

    if (this.context === context.searchModal) {
      searchDataStorage.setItem(this.product.id, {
        count: undefined,
        match: context.searchModal,
        query:
          analyticsEventBus.EVENTS.PAYLOADS.searchedTermNoText.type.featured,
      });
    }
  },

  methods: {
    handleScrollIntoView(isVisible) {
      if (this.hasPlpSliderBannerContext) return;

      this.handleVisibilityChanged(isVisible);

      if (isVisible) {
        // Fire analytics only on the first impression
        if (this.isFirstTimeInViewport) {
          this.isFirstTimeInViewport = false;
          this.analyticsPublishProductViewedEvent(this.product, {
            recommender: {
              isEinstein: this.isRecommendationFromEinstein,
            },
          });
        }
        // Re-activate content if it was virtualized
        if (!this.isContentActive) {
          this.isContentActive = true;
        }
      } else if (this.virtualizeWhenOffscreen && this.isContentActive) {
        // Cache the current rendered height before destroying content
        this.cachedHeight = this.$el.offsetHeight;
        this.isContentActive = false;
      }
    },
    publishAnalyticsClickedEvent() {
      // Send info for Analytics
      if (this.hasPlpSliderBannerContext) {
        analyticsEventBus.publish(
          analyticsEventBus.EVENTS.productListBannerCarouselProductClicked,
          {
            pid: this.product.id,
            position: this.carouselIndex,
            type: plpMobileSlider,
          }
        );
      } else {
        this.analyticsPublishProductClickedEvent(this.product, {
          recommender: {
            isEinstein: this.isRecommendationFromEinstein,
          },
        });
      }
    },
    viewProductDetails() {
      this.publishAnalyticsClickedEvent();

      if (this.shouldOpenModalForPDP) {
        this.$emit('open-pdp-modal', this.product);
      } else {
        this.doIfSingleClick(() => {
          if (this.$router.history?.current?.path === this.url) {
            this.$eventBus.$emit('base-product-tile:current-url');
          } else {
            this.$router.push(this.url);
          }
        });
      }
    },
    updateProductColorVariant(color, expanded) {
      const { gridType } = this;

      const onTheFlySwatchChangingSupported = [
        'productList',
        'search',
      ].includes(gridType);
      if (onTheFlySwatchChangingSupported) {
        this.$store.dispatch(types[gridType].loadColorVariation, {
          color,
          type: gridType,
          index: this.gridIndex,
          expand: expanded,
        });
      }
    },
  },
};
</script>
